import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, X, Clock, MessageCircle, Heart } from 'lucide-react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { interestsApi } from '../../api/interests'
import type { InterestResponse } from '../../types'
import { formatDistanceToNow } from '../../utils/date'
import Avatar from '../../components/Avatar'

type Tab = 'received' | 'sent'

function StatusBadge({ status }: { status: InterestResponse['status'] }) {
  const map: Record<InterestResponse['status'], string> = {
    PENDING: 'badge badge-pending',
    ACCEPTED: 'badge badge-accepted',
    DECLINED: 'badge badge-declined',
  }
  const labels: Record<InterestResponse['status'], string> = {
    PENDING: 'Pending',
    ACCEPTED: 'Accepted',
    DECLINED: 'Declined',
  }
  return <span className={map[status]}>{labels[status]}</span>
}

export default function InterestsPage() {
  const [tab, setTab] = useState<Tab>('received')
  const qc = useQueryClient()

  const { data: received, isLoading: loadingReceived } = useQuery({
    queryKey: ['interests', 'received'],
    queryFn: () => interestsApi.getReceived().then((r) => r.data.data),
  })

  const { data: sent, isLoading: loadingSent } = useQuery({
    queryKey: ['interests', 'sent'],
    queryFn: () => interestsApi.getSent().then((r) => r.data.data),
  })

  const acceptMut = useMutation({
    mutationFn: (id: string) => interestsApi.acceptInterest(id),
    onSuccess: () => {
      toast.success('Interest accepted! You can now chat.')
      qc.invalidateQueries({ queryKey: ['interests'] })
    },
    onError: () => toast.error('Failed to accept'),
  })

  const declineMut = useMutation({
    mutationFn: (id: string) => interestsApi.declineInterest(id),
    onSuccess: () => {
      toast.success('Interest declined')
      qc.invalidateQueries({ queryKey: ['interests'] })
    },
    onError: () => toast.error('Failed to decline'),
  })

  const withdrawMut = useMutation({
    mutationFn: (id: string) => interestsApi.withdrawInterest(id),
    onSuccess: () => {
      toast.success('Interest withdrawn')
      qc.invalidateQueries({ queryKey: ['interests'] })
    },
    onError: () => toast.error('Failed to withdraw'),
  })

  const interests = tab === 'received' ? received : sent
  const isLoading = tab === 'received' ? loadingReceived : loadingSent
  const pendingCount = received?.filter((i) => i.status === 'PENDING').length ?? 0

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h1 className="font-display font-semibold text-[30px] text-ink-900 mb-5">Interests</h1>

      {/* Tabs */}
      <div className="tab-bar mb-5">
        {(['received', 'sent'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`tab-btn capitalize ${tab === t ? 'tab-active' : 'tab-inactive'}`}
          >
            {t === 'received' ? (
              <span className="flex items-center justify-center gap-1.5">
                Received
                {pendingCount > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-maroon-700 text-champagne rounded-full ring-1 ring-gold-500/50">
                    {pendingCount}
                  </span>
                )}
              </span>
            ) : (
              'Sent'
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 animate-pulse bg-ivory-100 rounded-2xl" />
          ))}
        </div>
      ) : interests?.length ? (
        <div className="space-y-3">
          {interests.map((interest) => {
            const displayName = tab === 'sent'
              ? (interest.receiverName ?? 'Unknown')
              : (interest.senderName ?? interest.senderPhone ?? 'Unknown')
            const isAccepted = interest.status === 'ACCEPTED'
            const profileId = tab === 'sent' ? interest.receiverProfileId : interest.senderProfileId
            const avatarUrl = tab === 'sent' ? interest.receiverAvatarUrl : interest.senderAvatarUrl
            const profileLink = profileId ? `/profile/${profileId}` : null
            const avatar = <Avatar url={avatarUrl} name={displayName} className="w-12 h-12" />
            return (
              <div
                key={interest.id}
                className={`card p-4 flex items-center gap-4 ${
                  isAccepted ? 'border-l-2 border-l-success' : ''
                }`}
              >
                {/* Avatar — tappable link to the other person's profile */}
                {profileLink ? (
                  <Link to={profileLink} className="flex-shrink-0">{avatar}</Link>
                ) : (
                  avatar
                )}

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {profileLink ? (
                      <Link to={profileLink} className="font-display font-semibold text-ink-900 text-[16px] truncate hover:text-maroon-700">
                        {displayName}
                      </Link>
                    ) : (
                      <p className="font-display font-semibold text-ink-900 text-[16px] truncate">{displayName}</p>
                    )}
                    <StatusBadge status={interest.status} />
                  </div>
                  {interest.message && (
                    <p className="text-xs text-ink-400 italic mt-0.5 line-clamp-1">"{interest.message}"</p>
                  )}
                  <p className="text-[11px] text-ink-400 mt-0.5 flex items-center gap-1">
                    <Clock size={10} /> {formatDistanceToNow(interest.createdAt)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {tab === 'received' && interest.status === 'PENDING' && (
                    <>
                      <button
                        onClick={() => acceptMut.mutate(interest.id)}
                        className="w-9 h-9 rounded-full bg-success text-white hover:opacity-90 flex items-center justify-center transition-opacity"
                        title="Accept"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={() => declineMut.mutate(interest.id)}
                        className="w-9 h-9 rounded-full bg-ivory-100 hover:bg-ivory-200 text-ink-400 flex items-center justify-center transition-colors"
                        title="Decline"
                      >
                        <X size={16} />
                      </button>
                    </>
                  )}
                  {interest.status === 'ACCEPTED' && interest.matchId && (
                    <Link
                      to="/chat"
                      className="text-[11px] font-semibold uppercase tracking-[0.12em] text-maroon-700 hover:underline flex items-center gap-1"
                      title="Open chat"
                    >
                      <MessageCircle size={16} />
                    </Link>
                  )}
                  {tab === 'sent' && interest.status === 'PENDING' && (
                    <button
                      onClick={() => withdrawMut.mutate(interest.id)}
                      className="text-[11px] font-semibold uppercase tracking-wide text-ink-400 hover:text-danger transition-colors"
                    >
                      Withdraw
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-14 h-14 rounded-full bg-ivory-100 border border-gold-500/30 flex items-center justify-center">
            <Heart size={22} strokeWidth={1.5} className="text-maroon-700/50" />
          </div>
          <p className="font-display font-semibold text-lg text-ink-900">No {tab} interests yet</p>
          <p className="text-xs text-ink-400">
            {tab === 'received'
              ? 'When someone sends you interest, it will appear here'
              : 'Browse matches and send interests to connect'}
          </p>
        </div>
      )}
    </div>
  )
}
